import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserType } from '../types/auth';
import { toast } from 'sonner';
import { FcGoogle } from 'react-icons/fc';
import { HiOutlineUser, HiOutlineOfficeBuilding } from 'react-icons/hi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const Register: React.FC = () => {
  const { signInWithGoogle, signUpWithEmail, user, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userType, setUserType] = useState<UserType>('user');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  
  // Debug information
  console.log('Register component rendered');
  console.log('Auth state:', { user, loading, error });
  console.log('Current location:', location.pathname);
  
  useEffect(() => {
    // If user is already logged in, redirect to home
    if (user) {
      console.log('User already logged in, redirecting to home');
      navigate('/');
    }
  }, [user, navigate]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    // User specific fields
    gender: '',
    address: '',
    // Organizer specific fields
    website: '',
    description: '',
    businessType: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUserTypeChange = (type: UserType) => {
    setUserType(type);
  };

  // Helper to get required fields for each type
  const getRelevantFields = () => {
    if (userType === 'user') {
      return {
        name: displayName,
        phoneNumber: formData.phoneNumber,
        gender: formData.gender,
        address: formData.address,
      };
    } else {
      return {
        name: displayName,
        phoneNumber: formData.phoneNumber,
        website: formData.website,
        description: formData.description,
        businessType: formData.businessType,
        address: formData.address,
      };
    }
  };

  // Enhanced Google sign-in for registration
  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      // Step 1: Sign in with Google (no extra params)
      const googleUser = await signInWithGoogle();
      if (!googleUser) throw new Error('Google sign-in failed');

      // Step 2: Check Firestore for required fields
      const userRef = doc(db, 'users', googleUser.uid);
      const userSnap = await getDoc(userRef);
      let userData = userSnap.exists() ? userSnap.data() : {};
      const relevantFields = getRelevantFields();
      const missingFields = Object.entries(relevantFields).filter(([key, value]) => !userData[key] && !value);

      // Step 3: If missing fields, prompt user to complete them (simple prompt for now)
      if (missingFields.length > 0) {
        for (const [key] of missingFields) {
          // eslint-disable-next-line no-alert
          const value = window.prompt(`Please enter your ${key}:`);
          if (!value) {
            toast.error(`Registration cancelled: ${key} is required.`);
            setIsLoading(false);
            return;
          }
          relevantFields[key] = value;
        }
        // Update Firestore with missing fields
        await setDoc(userRef, {
          ...userData,
          ...relevantFields,
          userType,
          roles: userType === 'organizer' ? ['user', 'organizer'] : ['user'],
        }, { merge: true });
      }
      toast.success(`Successfully signed in as ${userType === 'user' ? 'a user' : 'an organizer'}`);
      navigate(userType === 'organizer' ? '/dashboard' : '/profile');
    } catch (error: any) {
      console.error('Failed to sign in with Google:', error);
      toast.error(error.message || 'Failed to sign in with Google');
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced handleSubmit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !displayName) {
      toast.error('Please fill out all fields.');
      return;
    }
    // Validate required fields for selected type
    const relevantFields = getRelevantFields();
    for (const [key, value] of Object.entries(relevantFields)) {
      if (!value) {
        toast.error(`Please fill out the ${key} field.`);
        return;
      }
    }
    try {
      setIsLoading(true);
      const user = await signUpWithEmail(email, password, displayName, userType, relevantFields);
      if (user) {
        toast.success('Registration successful! Welcome.');
        navigate(userType === 'organizer' ? '/dashboard' : '/profile');
      }
    } catch (err: any) {
      toast.error(err.message || 'Registration failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Or{' '}
          <Link to="/login" className="font-medium text-primary hover:text-primary/90">
            sign in to your existing account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* User Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              I want to register as:
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleUserTypeChange('user')}
                className={`flex items-center justify-center py-3 px-4 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                  userType === 'user'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-input hover:bg-accent'
                }`}
              >
                <HiOutlineUser className="mr-2 h-5 w-5" />
                Regular User
              </button>
              <button
                type="button"
                onClick={() => handleUserTypeChange('organizer')}
                className={`flex items-center justify-center py-3 px-4 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                  userType === 'organizer'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-input hover:bg-accent'
                }`}
              >
                <HiOutlineOfficeBuilding className="mr-2 h-5 w-5" />
                Organization
              </button>
            </div>
          </div>

          {/* Google Sign In Button */}
          <div className="mb-6">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex justify-center items-center py-2 px-4 border border-input rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <FcGoogle className="h-5 w-5 mr-2" />
              Continue with Google
            </button>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-input"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Common fields */}
            <div>
              <Label htmlFor="displayName">{userType === 'organizer' ? 'Organization Name' : 'Full Name'}</Label>
              <Input
                id="displayName"
                name="displayName"
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            {/* User-specific fields */}
            {userType === 'user' && (
              <>
                <div>
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select onValueChange={value => handleInputChange({ target: { name: 'gender', value } })} defaultValue={formData.gender}>
                    <SelectTrigger id="gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </>
            )}
            {/* Organizer-specific fields */}
            {userType === 'organizer' && (
              <>
                <div>
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    name="description"
                    type="text"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="businessType">Business Type</Label>
                  <Input
                    id="businessType"
                    name="businessType"
                    type="text"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </>
            )}
            {error && <p className="text-sm text-red-500">{error.message}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register; 